import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PTAFormField from 'Components/PTAFormField'
import { WithLoader } from 'Components/WithLoader'
import SalesPointDrawer from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointDrawer'
import SalesPointList from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointList'
import useSalesPointsWithParams from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks'
import { useDeleteSingleSalesPointMutation } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import { useSelectedPta } from 'Util/hooks/useSelectedPta'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY = 'salesPointDetailsDrawerTab'

export default function SalesPointListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`
  const navigate = useNavigate()
  const {
    salesPoints,
    isFetching: isSalesPointsFetching,
    totalResults,
    searchQueryParams,
    setSearchQueryParams,
    selectedSalesPointId,
    setSelectedSalesPointId,
    selectedPta,
    setSelectedPta
  } = useSalesPointsWithParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSalesPointId)

  const [deleteSalesPoint, { isLoading: isDeleteLoading }] = useDeleteSingleSalesPointMutation()

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Sales points`
    }
  ]

  const {
    searchText,
    handleChangePage,
    handleRequestSort,
    handleSearchTextChange,
    handleSearch,
    handlePTAChange
  } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const handleDrawerClose = () => {
    setSelectedSalesPointId('')
    setSelectedPta('')
    setIsDrawerOpen(false)
    searchParams.delete(SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string, pta: string) => {
    setSelectedSalesPointId(id)
    setSelectedPta(pta)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedSalesPointId || !selectedPta) return
    deleteSalesPoint({ sales_point_id: selectedSalesPointId, pta: selectedPta })
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Sales point successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the sales point.`)
      })
  }

  useSelectedPta(searchQueryParams.pta ?? selectedPta ?? '')

  const isLoading = isSalesPointsFetching || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Sales points`}</Typography>
        <Box sx={{ mt: 4 }}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new sales point`}
            </CustomButton>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <PTAFormField
                  value={searchQueryParams.pta}
                  onChange={handlePTAChange}
                  required={false}
                  displayEmptyFirstOption={true}
                />
              )}
              <ListSearch
                searchText={searchText}
                handleSearchTextChange={handleSearchTextChange}
                handleSearch={handleSearch}
                placeholder={t`Search by name or code`}
              />
            </Box>
          </ListActions>
          <Box sx={{ pt: 1 }}>
            <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
              <SalesPointList
                rows={salesPoints}
                isLoading={isLoading}
                params={searchQueryParams}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={totalResults}
                page={
                  searchQueryParams.start_index
                    ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                onRowClick={handleRowClick}
                selectedSalesPointId={selectedSalesPointId}
              />
            </WithLoader>
          </Box>
        </Box>
        <SalesPointDrawer
          open={isDrawerOpen}
          handleClose={handleDrawerClose}
          handleSalesPointDelete={handleDelete}
          selectedSalesPointId={selectedSalesPointId}
        />
      </Box>
    </Box>
  )
}
